import React, { useEffect, useState } from "react";
import { Receipt, ArrowUpRight, ArrowDownLeft } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { useBlockNav, useTxNav, usePickAsset } from "@/lib/navContext";
import { cn, formatLovelace, truncMid, unixToDate } from "@/lib/format";
import { Badge, CopyBtn, RowShareBtn } from "@/components/primitives";

const TxHashCard = ({ hash, index, selected, onSelect }) => {
  const onPickTx = useTxNav();
  return (
    <div
      data-testid={`tx-hash-card-${index}`}
      role="button"
      onClick={onSelect}
      className={cn(
        "group flex items-center gap-3 rounded-xl border bg-card/30 backdrop-blur-md px-4 py-3 cursor-pointer transition-colors",
        selected
          ? "border-[#22d3ee]/70 bg-[#22d3ee]/[0.06]"
          : "border-primary/20 hover:border-[#22d3ee]/50 hover:bg-primary/[0.03]",
      )}
    >
      <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-primary/25 bg-primary/[0.06]">
        <Receipt size={16} className="text-[#22d3ee]" />
      </div>
      <button
        type="button"
        data-testid="tx-hash-link"
        onClick={(e) => { e.stopPropagation(); onPickTx?.(hash); }}
        title="Open full transaction"
        className="min-w-0 text-left font-mono text-[13px] text-[#22d3ee] break-all hover:underline decoration-dotted underline-offset-2"
      >
        {truncMid(hash, 18, 8)}
      </button>
      <div className="flex-1 min-w-0 self-stretch" aria-hidden="true" />
      <Badge tone="success">confirmed</Badge>
      <CopyBtn value={hash} label="hash" />
      <RowShareBtn modeKey="transactions" id={hash} />
    </div>
  );
};

const MiniRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-primary/10 last:border-b-0">
    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono shrink-0 pt-0.5">{label}</span>
    <span className="mono text-[13px] tabnum text-right break-all min-w-0">{children}</span>
  </div>
);

// Block link that uses the address-nav-style approach by switching to blocks mode.
const TxHashCellBlockLink = ({ height }) => {
  const pickBlock = useBlockNav();
  if (height == null) return <span className="text-muted-foreground">—</span>;
  if (!pickBlock) return <span className="text-[#22d3ee]">#{height.toLocaleString("en-US")}</span>;
  return (
    <button
      type="button"
      data-testid="mini-block-link"
      onClick={() => pickBlock(String(height))}
      className="text-[#22d3ee] hover:underline decoration-dotted underline-offset-2"
    >
      #{height.toLocaleString("en-US")}
    </button>
  );
};

// Decode a Cardano asset `unit` (56-hex policy id + asset-name hex) into a
// readable ticker when the name is printable ascii, else a short policy label.
const assetLabel = (unit) => {
  const nameHex = unit.length > 56 ? unit.slice(56) : "";
  let name = "";
  for (let i = 0; i < nameHex.length; i += 2) {
    const c = parseInt(nameHex.slice(i, i + 2), 16);
    if (c >= 32 && c < 127) name += String.fromCharCode(c);
    else { name = ""; break; }
  }
  if (name) return name;
  return `${unit.slice(0, 8)}…${nameHex ? nameHex.slice(-6) : unit.slice(-6)}`;
};

const TxDetailsMini = ({ hash, amountMode, address }) => {
  const pickAsset = usePickAsset();
  const [data, setData] = useState(null);
  const [utxos, setUtxos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hash) return;
    let cancelled = false;
    setLoading(true); setError(null); setData(null); setUtxos(null);
    Promise.allSettled([
      http.get(`${API}/txs/${hash}`),
      http.get(`${API}/txs/${hash}/utxos`),
    ])
      .then(([tx, ux]) => {
        if (cancelled) return;
        if (tx.status === "fulfilled") setData(tx.value.data);
        else setError(tx.reason?.response?.data?.detail || { message: tx.reason?.message });
        if (ux.status === "fulfilled") setUtxos(ux.value.data);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hash]);

  if (!hash) {
    return (
      <div className="rounded-2xl border border-primary/15 bg-card/20 p-8 text-[12px] text-muted-foreground/60 mono min-h-[260px] flex items-center justify-center text-center">
        Select a transaction to see details
      </div>
    );
  }

  const lovelace = data?.output_amount?.find?.((a) => a.unit === "lovelace")?.quantity || "0";
  const confirmedWithin = data?.block_time
    ? `within ${Math.max(1, Math.floor(Date.now() / 1000 - data.block_time))} secs`
    : null;

  // Direction (incoming / outgoing) relative to the address being viewed.
  const lovelaceOf = (amt) => Number(amt?.find?.((a) => a.unit === "lovelace")?.quantity || 0);
  let direction = null;
  if (utxos && address) {
    const inFrom = (utxos.inputs || []).filter((i) => i.address === address).reduce((s, i) => s + lovelaceOf(i.amount), 0);
    const outTo = (utxos.outputs || []).filter((o) => o.address === address).reduce((s, o) => s + lovelaceOf(o.amount), 0);
    if (inFrom > 0 || outTo > 0) {
      const net = outTo - inFrom;
      direction = net < 0 ? { kind: "out", amount: String(Math.abs(net)) }
        : net > 0 ? { kind: "in", amount: String(net) }
        : { kind: "self" };
    }
  }

  // Distinct native assets moved in the tx (prefer outputs; fall back to inputs).
  const assetMap = {};
  const scan = (arr) => (arr || []).forEach((u) => (u.amount || []).forEach((a) => {
    if (a.unit !== "lovelace") {
      try { assetMap[a.unit] = (assetMap[a.unit] || 0n) + BigInt(a.quantity || 0); } catch {}
    }
  }));
  if (utxos) { scan(utxos.outputs); if (Object.keys(assetMap).length === 0) scan(utxos.inputs); }
  const assets = Object.entries(assetMap);
  const minted = (data?.asset_mint_or_burn_count ?? 0) > 0;

  return (
    <div data-testid="tx-details-mini" className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow min-h-[260px]">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
      <div className="px-6 py-4">
        <h3 className="font-heading text-[15px] md:text-[16px] text-[#22d3ee] text-glow-cyan mb-3">Transaction Details</h3>
        {loading && !data ? (
          <div className="space-y-3 py-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-4 bg-primary/10 rounded-sm animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-[12px] mono text-rose-400 py-4">{error.message || "Failed to load transaction"}</div>
        ) : data ? (
          <>
            <MiniRow label="Hash">
              <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                <span className="text-foreground/90 break-all">{truncMid(data.hash, 22, 10)}</span>
                <CopyBtn value={data.hash} label="tx-hash" />
              </span>
            </MiniRow>
            <MiniRow label="Status">
              <span className="inline-flex items-center gap-2">
                <Badge tone="success">confirmed</Badge>
                {confirmedWithin ? <span className="text-muted-foreground/80 normal-case text-[11px]">({confirmedWithin})</span> : null}
              </span>
            </MiniRow>
            {direction ? (
              <MiniRow label="Direction">
                {direction.kind === "out" ? (
                  <span data-testid="tx-direction" className="inline-flex items-center gap-2">
                    <ArrowUpRight size={14} className="text-rose-400" weight="bold" />
                    <span className="uppercase text-[11px] tracking-[0.15em] text-rose-400">Outgoing</span>
                    <span className="text-rose-400">−{formatLovelace(direction.amount, amountMode)}</span>
                  </span>
                ) : direction.kind === "in" ? (
                  <span data-testid="tx-direction" className="inline-flex items-center gap-2">
                    <ArrowDownLeft size={14} className="text-[#22d3ee]" weight="bold" />
                    <span className="uppercase text-[11px] tracking-[0.15em] text-[#22d3ee]">Incoming</span>
                    <span className="text-[#22d3ee]">+{formatLovelace(direction.amount, amountMode)}</span>
                  </span>
                ) : (
                  <span data-testid="tx-direction" className="uppercase text-[11px] tracking-[0.15em] text-muted-foreground">Internal (self)</span>
                )}
              </MiniRow>
            ) : null}
            <MiniRow label="Time">
              <span className="text-foreground">{data.block_time ? unixToDate(data.block_time) : "—"}</span>
            </MiniRow>
            <MiniRow label="Block">
              <TxHashCellBlockLink height={data.block_height} />
            </MiniRow>
            <MiniRow label="Total Output">
              <span className="text-[#22d3ee] text-glow-cyan">{formatLovelace(lovelace, amountMode)}</span>
            </MiniRow>
            <MiniRow label="Total Fees">
              <span className="text-orange-400">{formatLovelace(data.fees, amountMode)}</span>
            </MiniRow>
            <MiniRow label="Assets">
              {assets.length === 0 ? (
                <span data-testid="tx-assets" className="text-muted-foreground">{minted ? "mint / burn only" : "ADA only"}</span>
              ) : (
                <span data-testid="tx-assets" className="inline-flex flex-col items-end gap-1">
                  {assets.slice(0, 4).map(([unit, qty]) => (
                    <span key={unit} className="inline-flex items-center gap-2" title={unit}>
                      <button
                        type="button"
                        data-testid="tx-asset-link"
                        onClick={(e) => { e.stopPropagation(); pickAsset?.(unit); }}
                        title="Open this token"
                        className="px-2 py-0.5 rounded border border-primary/30 bg-primary/5 text-primary text-[11px] tracking-[0.05em] hover:border-[#22d3ee]/70 hover:bg-[#22d3ee]/10 hover:text-[#22d3ee] transition-colors cursor-pointer"
                      >
                        {assetLabel(unit)}
                      </button>
                      <span className="text-foreground/90">×{qty.toString()}</span>
                    </span>
                  ))}
                  {assets.length > 4 ? <span className="text-[11px] text-muted-foreground">+{assets.length - 4} more</span> : null}
                  {minted ? <span className="text-[11px] text-orange-400 normal-case">includes mint / burn</span> : null}
                </span>
              )}
            </MiniRow>
            <MiniRow label="Certificates">
              <span className="text-foreground">{data.stake_cert_count ?? 0}</span>
            </MiniRow>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ---------- transaction hashes list + detail preview (used by tx "hashes" shape) ----------
export const TxHashesList = ({ rows, loading, amountMode, address }) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Auto-select first row when the list (re)loads; clear if it vanished.
    if (rows && rows.length) {
      setSelected((prev) => (prev && rows.some((r) => r.value === prev) ? prev : rows[0].value));
    } else {
      setSelected(null);
    }
  }, [rows]);

  if (loading && (!rows || rows.length === 0)) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-[60px] rounded-xl border border-primary/15 bg-card/30 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <div className="text-[12px] text-muted-foreground/60 mono py-6">no transactions</div>;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_1fr] gap-4 items-start">
      <div data-testid="tx-hashes-list" className="space-y-2 lg:max-h-[640px] lg:overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <TxHashCard
            key={`${r.value}-${i}`}
            hash={r.value}
            index={i}
            selected={selected === r.value}
            onSelect={() => setSelected(r.value)}
          />
        ))}
      </div>
      <div className="lg:sticky lg:top-4">
        <TxDetailsMini hash={selected} amountMode={amountMode} address={address} />
      </div>
    </div>
  );
};
